import React, { useEffect, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Flame, CalendarClock, PencilLine, RotateCcw, ChevronRight, Eye } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { useIconScale } from '../state/useIconScale';
import { diasHasta } from '../state/format';
import { EstadoBolsa } from '../state/bolsaMachine';
import { BolsaCentro } from '../types';
import { centroApi } from '../api/centroApi';

interface AttentionListProps {
  cotizaciones: BolsaCentro[];
  onAbrirDrawer: (id: string) => void;
  onVerDetalle: (id: string) => void;
}

interface Item {
  id: string;
  icono: React.ReactNode;
  color: string;
  titulo: string;
  subtitulo: string;
  prioridad: number;
}

export const AttentionList: React.FC<AttentionListProps> = ({ cotizaciones, onAbrirDrawer, onVerDetalle }) => {
  // BORRADOR con envío previo ("en ajuste") requiere saber si ya tiene versiones enviadas —
  // dato no incluido en el listado del Centro; se resuelve con una consulta puntual por
  // candidato (conjunto acotado a la escala comercial del proyecto, ver CONTEXT.md §1).
  const [versionesPorBorrador, setVersionesPorBorrador] = useState<Record<string, number>>({});
  const attentionIconSize = useIconScale(16);
  const chevronRightSize = useIconScale(15);

  const borradores = cotizaciones.filter((b) => b.estado_codigo === EstadoBolsa.BORRADOR && b.codigo_cotizacion);

  useEffect(() => {
    let cancelado = false;
    Promise.all(
      borradores.map(async (b) => {
        try {
          const detalle = await centroApi.obtenerDetalle(b.id);
          return [b.id, detalle.versiones.length] as const;
        } catch {
          return [b.id, 0] as const;
        }
      }),
    ).then((resultados) => {
      if (cancelado) return;
      setVersionesPorBorrador(Object.fromEntries(resultados));
    });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotizaciones]);

  const items: Item[] = [];

  for (const b of cotizaciones) {
    if (b.estado_codigo === EstadoBolsa.ENVIADA && b.validez) {
      const dias = diasHasta(b.validez);
      if (dias <= 3) {
        items.push({
          id: b.id,
          icono: <Flame size={attentionIconSize} color={TOKENS.red} />,
          color: TOKENS.red,
          titulo: b.codigo_cotizacion ?? b.id,
          subtitulo: `Vence en ${dias} día${dias === 1 ? '' : 's'}`,
          prioridad: 1,
        });
      } else if (dias <= 7) {
        items.push({
          id: b.id,
          icono: <CalendarClock size={attentionIconSize} color={TOKENS.amber} />,
          color: TOKENS.amber,
          titulo: b.codigo_cotizacion ?? b.id,
          subtitulo: `Vence en ${dias} días`,
          prioridad: 2,
        });
      }
    }
  }

  for (const b of borradores) {
    const versiones = versionesPorBorrador[b.id] ?? 0;
    if (versiones > 0) {
      items.push({
        id: b.id,
        icono: <PencilLine size={attentionIconSize} color={TOKENS.blue} />,
        color: TOKENS.blue,
        titulo: b.codigo_cotizacion ?? b.id,
        subtitulo: `en ajuste — pendiente reenviar · será v${versiones + 1}`,
        prioridad: 3,
      });
    }
  }

  for (const b of cotizaciones) {
    if (b.estado_codigo === EstadoBolsa.VENCIDA) {
      items.push({
        id: b.id,
        icono: <RotateCcw size={attentionIconSize} color={TOKENS.slate} />,
        color: TOKENS.slate,
        titulo: b.codigo_cotizacion ?? b.id,
        subtitulo: 'venció — evaluar reactivación',
        prioridad: 4,
      });
    }
  }

  const ordenados = items.sort((a, b) => a.prioridad - b.prioridad).slice(0, 5);

  return (
    <Box data-testid="attention-list">
      {/* SPEC-105 (CH-01) — título "Requiere atención" ahora lo provee el SectionCard que
          envuelve este componente en ResumenTab; se elimina el duplicado. */}
      {ordenados.length === 0 ? (
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), color: TOKENS.faint }}>
          Sin pendientes — pipeline al día.
        </Typography>
      ) : (
        ordenados.map((item) => (
          <Box
            key={item.id}
            data-testid="attention-item"
            onClick={() => onAbrirDrawer(item.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: '#fff',
              borderRadius: '14px',
              padding: '12px 14px',
              marginBottom: '10px',
              cursor: 'pointer',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,.65), 0 2px 4px rgba(46,36,48,.05), 0 6px 14px rgba(46,36,48,.08)',
              transition: 'transform .15s ease, box-shadow .15s ease',
              '&:hover': { transform: 'translateX(4px)', boxShadow: '0 3px 6px rgba(46,36,48,.07), 0 10px 22px rgba(46,36,48,.11)' },
            }}
          >
            <Box sx={{ width: 38, height: 38, borderRadius: '11px', backgroundColor: `${item.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {item.icono}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 500, color: TOKENS.ink }}>{item.titulo}</Typography>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.muted }}>{item.subtitulo}</Typography>
            </Box>
            {/* SPEC-114 (RN-001, RN-002, RN-003) — navega directo al detalle, sin abrir el drawer */}
            <Tooltip title="Ver detalle" arrow>
              <Box
                component="button"
                type="button"
                data-testid="attention-ver-detalle"
                aria-label="Ver detalle"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onVerDetalle(item.id);
                }}
                sx={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'transparent',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: TOKENS.muted,
                  '&:hover': { backgroundColor: 'rgba(147,64,143,.10)', color: TOKENS.purple },
                }}
              >
                <Eye size={attentionIconSize} strokeWidth={2} />
              </Box>
            </Tooltip>
            <ChevronRight size={chevronRightSize} color={TOKENS.faint} />
          </Box>
        ))
      )}
    </Box>
  );
};
